import { IAdminUser } from '@dadohr/shared/domainTypes/adminUser';
import { Button, Dialog, DialogActions, DialogContent, Typography } from '@material-ui/core';
import React, { useState } from 'react';

import { useAnnouncement, shouldShowAnnouncement } from '../../services/announcement';
import { useCurrentUser } from '../../services/currentUser';
import { callFirebaseFunction } from '../../services/firebaseFunctions';
import { DialogActionsHeader } from '../DialogActionsHeader';
import { VideoPlayer } from '../VideoPlayer';

/**
 * One-time feature-announcement video modal for dashboard admins. Self-gating (mounted at
 * the app root next to DadoCopilotGated): shows only when a super-admin has an active
 * announcement whose version the current user has not yet dismissed. Only the X and "Got
 * it" close it (backdrop/Escape do nothing); both mark the latest version seen so it never
 * reappears until the version is bumped again. Employees never see it (this lives in the
 * dashboard app, not 01-standalone-app).
 */
export const FeatureAnnouncementModal: React.FC = () => {
  const currentUser = useCurrentUser();
  const [announcement] = useAnnouncement();
  // seenAnnouncementVersion flows through the pass-through currentUser converter; the
  // published IAdminUser type may not include it yet, so read it via a localized cast.
  const seenVersion = (currentUser as IAdminUser & { seenAnnouncementVersion?: number })
    .seenAnnouncementVersion;

  // Local latch so the modal hides immediately on dismiss, before the callable round-trips
  // (the live currentUser listener then persists the seen version).
  const [dismissed, setDismissed] = useState(false);

  if (dismissed || !shouldShowAnnouncement(announcement, seenVersion) || !announcement) {
    return null;
  }

  const markSeen = () => {
    setDismissed(true);
    callFirebaseFunction('adminUser-markAnnouncementSeen', {
      version: announcement.version
    }).catch(() => undefined);
  };

  return (
    <Dialog
      open
      maxWidth="md"
      fullWidth
      disableBackdropClick
      disableEscapeKeyDown
    >
      <DialogActionsHeader
        left={
          <Typography variant="h4">{announcement.title || 'What’s new'}</Typography>
        }
        onClose={markSeen}
      />
      <DialogContent>
        <div style={{ paddingTop: 16, paddingBottom: 8 }}>
          <VideoPlayer url={announcement.videoUrl} />
        </div>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="primary" onClick={markSeen}>
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
};
